/* eslint-disable no-unreachable */
import {Form, Input, Select} from 'antd';
import styles from './mobileSetting.module.scss';
import {FeeSchedule} from 'components/feeSchedule/FeeSchedule';
import SubmitButton from 'components/Startup/components/SubmitButton';
import {useMutation, useQuery} from '@apollo/client';
import {TRANSLATE_TEXT} from 'graphql/mutations/notifications';
import {Button} from '@dabafinance/react-components';
import {toast} from 'react-toastify';
import {UPDATE_APP_CONFIGURATION} from 'graphql/queries/mobileSettings';
import {GET_ALL_STOCKS_WITH_INSTRUMENT_KEY} from 'graphql/queries/stocks';
import {useEffect, useState} from 'react';

/**
 * A discount is configured per instrument key, and may additionally be scoped to one
 * exchange — venues price differently, so the same instrument type can carry different
 * terms on each. A scoped entry declares `exchangeCode` (NGX / BRVM); an entry without
 * one applies on every venue.
 *
 * The stored value is therefore either a single entry (the original shape, still what
 * most keys hold) or a list of them. These helpers hide that difference so the form
 * only ever edits one entry at a time.
 */
const ALL_EXCHANGES = '__ALL__';

/** Whatever is stored for one instrument key, as a list. */
const asEntries = raw => (raw ? (Array.isArray(raw) ? raw : [raw]) : []);

/** The entry the given venue edits — unscoped when venue is ALL_EXCHANGES. */
const entryForVenue = (raw, venue) =>
  asEntries(raw).find(entry =>
    venue === ALL_EXCHANGES
      ? !entry?.exchangeCode
      : entry?.exchangeCode === venue,
  );

/**
 * Put `entry` back, replacing whatever that venue held and leaving every other venue
 * untouched. A null entry removes the venue's discount. Collapses back to a bare object
 * when only an unscoped entry is left, so a config that never used venues keeps the
 * exact shape it had.
 */
const withVenueEntry = (raw, venue, entry) => {
  const others = asEntries(raw).filter(existing =>
    venue === ALL_EXCHANGES
      ? existing?.exchangeCode
      : existing?.exchangeCode !== venue,
  );
  const next = entry ? [...others, entry] : others;
  if (!next.length) return undefined;
  if (next.length === 1 && !next[0].exchangeCode) return next[0];
  return next;
};

export const ProDiscountSettings = ({refetch, data}) => {
  const [form] = Form.useForm();
  const [mainTab, setMainTab] = useState('pro'); // 'pro' or 'retry'
  const [currentTab, setCurrentTab] = useState('PUBLIC_EQUITY');
  // Which venue's entry is being edited. ALL_EXCHANGES is the unscoped one that
  // applies everywhere, and is what every pre-existing config holds.
  const [venue, setVenue] = useState(ALL_EXCHANGES);

  const {data: exchangeData} = useQuery(GET_ALL_STOCKS_WITH_INSTRUMENT_KEY);
  const venueOptions = [
    {value: ALL_EXCHANGES, label: 'All exchanges'},
    ...(exchangeData?.getAllStocksExchange ?? [])
      .filter(exchange => exchange?.code)
      .map(exchange => ({
        value: exchange.code,
        label: `${exchange.code} — ${exchange.stockExchangeName}`,
      })),
  ];
  const description = Form.useWatch(
    discountTabs[currentTab][`${mainTab}EnfieldName`],
    form,
  );

  const [translateText, {loading: loadingTranslation}] =
    useMutation(TRANSLATE_TEXT);
  const [updateAppConfig, {loading}] = useMutation(UPDATE_APP_CONFIGURATION, {
    errorPolicy: 'all',
  });

  useEffect(() => {
    if (data) {
      // Reset first: switching venue must clear the previous venue's values rather
      // than leave them behind on a venue that has no discount configured.
      form.resetFields();
      form.setFieldsValue(mapDataToFormValues(data));
    }
  }, [data, form, venue]);

  const mapDataToFormValues = rawData => {
    const formValues = {};
    // Map pro data
    if (rawData?.pro) {
      Object.keys(discountTabs).forEach(tabKey => {
        const tab = discountTabs[tabKey.toUpperCase()];
        const tabData = entryForVenue(rawData.pro[tabKey], venue);

        if (tabData) {
          formValues[tab.proEnfieldName] = tabData.description?.EN || '';
          formValues[tab.proFrFieldName] = tabData.description?.FR || '';
          formValues[tab.proFeeName] =
            tabData.feeSchedule?.map(fees => ({
              ...fees,
              vector: fees?.vector?.map(vect => ({
                duration: vect?.duration,
                feeType: vect?.magnitude?.feeType,
                ...(vect?.magnitude?.feeType === 'FIXED' && {
                  fixedAmount: vect?.magnitude?.value,
                }),
                ...(vect?.magnitude?.feeType === 'VARIABLE' && {
                  variableAmount: vect?.magnitude?.value,
                }),
              })),
            })) || [];
        }
      });
    }
    // Map retry data
    if (rawData?.retry) {
      Object.keys(discountTabs).forEach(tabKey => {
        const tab = discountTabs[tabKey.toUpperCase()];
        const tabData = entryForVenue(rawData.retry[tabKey], venue);

        if (tabData) {
          formValues[tab.retryEnfieldName] = tabData.description?.EN || '';
          formValues[tab.retryFrFieldName] = tabData.description?.FR || '';
          formValues[tab.retryFeeName] =
            tabData.feeSchedule?.map(fees => ({
              ...fees,
              vector: fees?.vector?.map(vect => ({
                duration: vect?.duration,
                feeType: vect?.magnitude?.feeType,
                ...(vect?.magnitude?.feeType === 'FIXED' && {
                  fixedAmount: vect?.magnitude?.value,
                }),
                ...(vect?.magnitude?.feeType === 'VARIABLE' && {
                  variableAmount: vect?.magnitude?.value,
                }),
              })),
            })) || [];
        }
      });
    }
    return formValues;
  };

  const handleTranslation = () => {
    const fieldName = discountTabs[currentTab][`${mainTab}EnfieldName`];
    let sourceText = form.getFieldValue(fieldName);

    if (!sourceText) return;

    translateText({
      variables: {
        sourceLang: 'EN',
        targetLang: 'FR',
        sourceText,
      },
    })
      .then(({data: {translateText}}) => {
        if (
          translateText.statusCode === 400 ||
          translateText.__typename === 'Error'
        ) {
          toast.error('Error Translating Text', translateText.message);
        } else {
          toast.success('Translated Successfully');
          form.setFieldValue(
            discountTabs[currentTab][`${mainTab}FrFieldName`],
            translateText?.targetText,
          );
        }
      })
      .catch(err => toast.error(err));
  };

  const handleSubmit = values => {
    let settingData = {};
    const currentSettingData = data?.[mainTab] || {};
    let existingData = currentSettingData
      ? Object.keys(currentSettingData)
          .filter(key => key !== currentTab)
          .reduce((obj, key) => {
            obj[key] = currentSettingData[key];
            return obj;
          }, {})
      : {};

    Object.keys(discountTabs)
      .filter(key => key === currentTab)
      .forEach(tabKey => {
        const tab = discountTabs[tabKey];
        const enFieldName = tab[`${mainTab}EnfieldName`];
        const frFieldName = tab[`${mainTab}FrFieldName`];
        const feeFieldName = tab[`${mainTab}FeeName`];

        const descriptionEN = values[enFieldName];
        const descriptionFR = values[frFieldName];
        const feeSchedule = values[feeFieldName]?.map(data => ({
          feeName: data?.feeName,
          feeType: data?.feeType,
          ...(data?.fixedFeeAmount && {
            fixedFeeAmount: Number(data?.fixedFeeAmount),
          }),
          ...(data.feeType === 'ENTRY' && {level: data?.level}),
          ...(data?.variableFeeAmount && {
            variableFeeAmount: Number(data?.variableFeeAmount),
          }),
          ...(data.feeType === 'ENTRY' && {level: data?.level}),
          ...(data?.scalable !== null &&
            data?.scalable !== undefined && {scalable: data?.scalable}),
          ...(data?.feeType === 'RECURRING' &&
            data?.scalable && {
              frequency: data?.frequency,
              origination: data?.origination,
            }),
          ...(data?.feeType === 'EXIT' &&
            data?.scalable &&
            Array.isArray(data?.vector) && {
              vector: data?.vector?.map(vect => ({
                duration: vect?.duration,
                magnitude: {
                  feeType: vect?.feeType,
                  value:
                    vect?.feeType === 'FIXED'
                      ? vect?.fixedAmount
                      : vect?.variableAmount,
                },
              })),
            }),
        }));

        // An emptied form removes this venue's entry rather than writing a blank one.
        const entry =
          descriptionEN || descriptionFR || feeSchedule?.length
            ? {
                // Only a venue-scoped entry carries the code; the unscoped one must
                // not, or it would stop applying everywhere.
                ...(venue !== ALL_EXCHANGES && {exchangeCode: venue}),
                description: {
                  ...(descriptionEN && {EN: descriptionEN}),
                  ...(descriptionFR && {FR: descriptionFR}),
                },
                ...(feeSchedule?.length && {feeSchedule}),
              }
            : null;

        // Merge into what this instrument key already holds, so editing the NGX
        // discount cannot wipe the BRVM one or the everywhere one.
        const merged = withVenueEntry(currentSettingData[tabKey], venue, entry);
        // Sent even when empty: the backend merges per instrument key, so simply
        // omitting the key would leave the old discount standing instead of
        // clearing it.
        settingData[tabKey] = merged ?? null;
      });

    updateAppConfig({
      variables: {
        data: {
          discountSettings: {
            [mainTab]: {
              ...existingData,
              ...settingData,
            },
          },
        },
      },
    })
      .then(({data: {updateAppConfig}}) => {
        if (
          updateAppConfig.statusCode === 400 ||
          updateAppConfig.__typename === 'Error'
        ) {
          toast.error('Error Updating Config', updateAppConfig.message);
        } else {
          refetch();
          toast.success('Updated Successfully');
        }
      })
      .catch(err => toast.error(err.message || 'An error occurred'));
  };

  const isFieldRequired = (tabKey, formValues) => {
    const tab = discountTabs[tabKey];
    const enFieldName = tab[`${mainTab}EnfieldName`];
    const frFieldName = tab[`${mainTab}FrFieldName`];
    const feeFieldName = tab[`${mainTab}FeeName`];

    const enValue = formValues[enFieldName];
    const frValue = formValues[frFieldName];
    const feeSchedule = formValues[feeFieldName];

    const isValid = !!(
      enValue ||
      frValue ||
      (feeSchedule && feeSchedule.length > 0)
    );
    return isValid;
  };

  return (
    <div className={styles.body}>
      <h1>Daba Pro Plan Discount Settings</h1>

      <div className={styles.tip}>
        <p>
          <strong>NOTE: </strong>
          Discount works as a subtracted reduction from the total fee. This
          means if a variable fee of 3% is already set on the instrument a
          variable fee discount of 1% will simply bring the overall variable fee
          to 2%.{' '}
          <a
            target="_blank"
            rel="noreferrer"
            href="https://www.notion.so/investondaba/Stock-Trading-f60b8d750d504d08a6c8c7e17736c986?pvs=4#f2b60dfc93dc48c2a89ce28cfba96867">
            Link to Documentation
          </a>
        </p>
      </div>
      <div className={styles['details-toggler']}>
        <span
          onClick={() => setMainTab('pro')}
          className={mainTab === 'pro' ? styles['active-toggle'] : ''}>
          Discount Settings
        </span>
        <span
          onClick={() => setMainTab('retry')}
          className={mainTab === 'retry' ? styles['active-toggle'] : ''}>
          Retry Discount Settings
        </span>
      </div>
      <div className={styles['details-toggler']}>
        {Object.keys(discountTabs).map(tab => (
          <span
            onClick={() => setCurrentTab(tab)}
            className={tab === currentTab ? styles['active-toggle'] : ''}
            key={tab}>
            {discountTabs[tab]?.name}
          </span>
        ))}
      </div>
      <Form onFinish={handleSubmit} form={form} layout="vertical">
        <div>
          <Form.Item
            className={styles.proField}
            label="Applies to exchange"
            tooltip="Exchanges price differently, so the same instrument can carry different discount terms on each. 'All exchanges' is the discount that applies wherever no exchange-specific one is set — an exchange-specific one takes precedence over it on that venue. Each exchange is edited separately; saving one does not affect the others.">
            <Select
              value={venue}
              onChange={setVenue}
              options={venueOptions}
              style={{maxWidth: 420}}
            />
          </Form.Item>
          <div className={styles.translateField}>
            <Form.Item
              rules={[
                ({getFieldValue}) => ({
                  validator(_, value) {
                    const formValues = getFieldValue();
                    const isRequired = isFieldRequired(currentTab, formValues);
                    if (isRequired && !value) {
                      return Promise.reject(
                        new Error('This field is required'),
                      );
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
              className={styles.proField}
              name={discountTabs[currentTab][`${mainTab}EnfieldName`]}
              label={discountTabs[currentTab].enLabel}>
              <Input.TextArea rows={2} />
            </Form.Item>
            {description?.length > 5 && (
              <Button
                type="secondary"
                disabled={loadingTranslation}
                label="Translate"
                onClick={handleTranslation}
              />
            )}
          </div>
          <Form.Item
            rules={[
              ({getFieldValue}) => ({
                validator(_, value) {
                  const formValues = getFieldValue();
                  const isRequired = isFieldRequired(currentTab, formValues);
                  if (isRequired && !value) {
                    return Promise.reject(new Error('This field is required'));
                  }
                  return Promise.resolve();
                },
              }),
            ]}
            className={styles.proField}
            name={discountTabs[currentTab][`${mainTab}FrFieldName`]}
            label={discountTabs[currentTab].frLabel}>
            <Input.TextArea rows={2} />
          </Form.Item>
          <h2>{discountTabs[currentTab]?.name} Discount Schedule</h2>
          <FeeSchedule
            feeName={discountTabs[currentTab][`${mainTab}FeeName`]}
            form={form}
            isInstrument={currentTab === 'MUTUAL_FUND'}
            forMutualFund={currentTab === 'MUTUAL_FUND'}
          />
        </div>
        <SubmitButton disabled={loading} label="Update" />
      </Form>
    </div>
  );
};

const discountTabs = {
  // The canonical instrument key is PUBLIC_EQUITY. This tab was keyed 'STOCK', so it
  // read and wrote `discountSettings.pro.STOCK` — a slot no fee engine ever reads,
  // leaving the real listed-equity discount invisible and uneditable here.
  PUBLIC_EQUITY: {
    name: 'STOCK',
    value: 'PUBLIC_EQUITY',
    proEnfieldName: 'proEnStockDescription',
    proFrFieldName: 'proFrStockDescription',
    proFeeName: 'proStocksFeeSchedule',
    retryEnfieldName: 'retryEnStockDescription',
    retryFrFieldName: 'retryFrStockDescription',
    retryFeeName: 'retryStocksFeeSchedule',
    enLabel: 'Stock Description (EN)',
    frLabel: 'Stock Description (FR)',
  },
  BOND: {
    name: 'BOND',
    value: 'BOND',
    proEnfieldName: 'proEnBondDescription',
    proFrFieldName: 'proFrBondDescription',
    proFeeName: 'proBondFeeSchedule',
    retryEnfieldName: 'retryEnBondDescription',
    retryFrFieldName: 'retryFrBondDescription',
    retryFeeName: 'retryBondFeeSchedule',
    enLabel: 'Bond Description (EN)',
    frLabel: 'Bond Description (FR)',
  },
  VENTURE_FUND: {
    name: 'VENTURE FUND',
    value: 'VENTURE_FUND',
    proEnfieldName: 'proEnVentureFundDescription',
    proFrFieldName: 'proFrVentureFundDescription',
    proFeeName: 'proVentureFundFeeSchedule',
    retryEnfieldName: 'retryEnVentureFundDescription',
    retryFrFieldName: 'retryFrVentureFundDescription',
    retryFeeName: 'retryVentureFundFeeSchedule',
    enLabel: 'Venture Fund Description (EN)',
    frLabel: 'Venture Fund Description (FR)',
  },
  VENTURE: {
    name: 'VENTURE',
    value: 'VENTURE',
    proEnfieldName: 'proEnVentureDescription',
    proFrFieldName: 'proFrVentureDescription',
    proFeeName: 'proVentureFeeSchedule',
    retryEnfieldName: 'retryEnVentureDescription',
    retryFrFieldName: 'retryFrVentureDescription',
    retryFeeName: 'retryVentureFeeSchedule',
    enLabel: 'Venture Description (EN)',
    frLabel: 'Venture Description (FR)',
  },
  IPO: {
    name: 'IPO',
    value: 'IPO',
    proEnfieldName: 'proEnIPODescription',
    proFrFieldName: 'proFrIPODescription',
    proFeeName: 'proIPOFeeSchedule',
    retryEnfieldName: 'retryEnIPODescription',
    retryFrFieldName: 'retryFrIPODescription',
    retryFeeName: 'retryIPOFeeSchedule',
    enLabel: 'IPO Description (EN)',
    frLabel: 'IPO Description (FR)',
  },
  MUTUAL_FUND: {
    name: 'MUTUAL FUND',
    value: 'MUTUAL_FUND',
    proEnfieldName: 'proEnMutualFundDescription',
    proFrFieldName: 'proFrMutualFundDescription',
    proFeeName: 'proMutualFundFeeSchedule',
    retryEnfieldName: 'retryEnMutualFundDescription',
    retryFrFieldName: 'retryFrMutualFundDescription',
    retryFeeName: 'retryMutualFundFeeSchedule',
    enLabel: 'Mutual Fund Description (EN)',
    frLabel: 'Mutual Fund Description (FR)',
  },
};
